import { ApiService, type ApiRequestConfig } from "./api.service";
import type { ICustomRole, IWorkspaceRoleDraft } from "@/types/workspace";
import type { IPermissionCatalogue } from "@/types/permission-catalogue";
import type { TRoleEligibility, TRoleEligibilityAnswers } from "@/types/role-eligibility";
import type {
  TAccessMapVersion,
  TMemberReachKind,
  TRoleAccessMap,
  TRoleMemberItems,
  TRoleMemberReach,
} from "@/types/role-access-map";

export class WorkspaceRoleService extends ApiService {
  async listRoles(slug: string, config?: ApiRequestConfig): Promise<ICustomRole[]> {
    return this.get(`/api/workspaces/${slug}/custom-roles/`, config);
  }

  /** The permission catalogue for a tier (NG-1272).
   *
   *  `tier` decides which permissions come back `available` and which come back
   *  `locked`, so a role's tier has to be passed — the default is admin, for
   *  which nothing is locked, and rendering that for a Guest role would offer
   *  toggles the tier does not sell.
   */
  async getPermissionCatalogue(
    slug: string,
    tier: string,
    config?: ApiRequestConfig,
  ): Promise<IPermissionCatalogue> {
    return this.get(
      `/api/workspaces/${slug}/permissions/catalogue/?tier=${encodeURIComponent(tier)}`,
      config,
    );
  }

  /** What a role reaches (NG-3939), from its draft or its published row. */
  async getRoleAccessMap(
    slug: string,
    roleId: string,
    version: TAccessMapVersion,
    config?: ApiRequestConfig,
  ): Promise<TRoleAccessMap> {
    return this.get(
      `/api/workspaces/${slug}/custom-roles/${roleId}/access-map/?version=${version}`,
      config,
    );
  }

  async getRoleMemberReach(
    slug: string,
    roleId: string,
    userId: string,
    config?: ApiRequestConfig,
  ): Promise<TRoleMemberReach> {
    return this.get(
      `/api/workspaces/${slug}/custom-roles/${roleId}/access-map/members/${userId}/`,
      config,
    );
  }

  async getRoleMemberItems(
    slug: string,
    roleId: string,
    userId: string,
    page: { kind: TMemberReachKind; q: string; offset: number; limit: number },
    config?: ApiRequestConfig,
  ): Promise<TRoleMemberItems> {
    const params = new URLSearchParams({
      kind: page.kind,
      offset: String(page.offset),
      limit: String(page.limit),
    });
    if (page.q) params.set("q", page.q);
    return this.get(
      `/api/workspaces/${slug}/custom-roles/${roleId}/access-map/members/${userId}/items/?${params.toString()}`,
      config,
    );
  }

  async createRole(slug: string, data: Partial<ICustomRole>): Promise<ICustomRole> {
    return this.post(`/api/workspaces/${slug}/custom-roles/`, data);
  }

  /** NG-1901. The server decides what the copy carries (name, fields, no
   *  members, never the Default badge) — send nothing but the source id. */
  async duplicateRole(slug: string, roleId: string): Promise<ICustomRole> {
    return this.post(`/api/workspaces/${slug}/custom-roles/${roleId}/duplicate/`);
  }

  async updateRole(slug: string, roleId: string, data: Partial<ICustomRole>): Promise<ICustomRole> {
    return this.patch(`/api/workspaces/${slug}/custom-roles/${roleId}/`, data);
  }

  async getRoleDraft(
    slug: string,
    roleId: string,
    config?: ApiRequestConfig,
  ): Promise<IWorkspaceRoleDraft> {
    return this.get(`/api/workspaces/${slug}/custom-roles/${roleId}/draft/`, config);
  }

  async updateRoleDraft(
    slug: string,
    roleId: string,
    data: Partial<IWorkspaceRoleDraft>,
  ): Promise<IWorkspaceRoleDraft> {
    return this.patch(`/api/workspaces/${slug}/custom-roles/${roleId}/draft/`, data);
  }

  async discardRoleDraft(slug: string, roleId: string): Promise<IWorkspaceRoleDraft> {
    return this.post(`/api/workspaces/${slug}/custom-roles/${roleId}/draft/discard/`, {});
  }

  async publishRoleDraft(slug: string, roleId: string): Promise<IWorkspaceRoleDraft> {
    return this.post(`/api/workspaces/${slug}/custom-roles/${roleId}/draft/publish/`, {});
  }

  /** Who the role's holders may be assigned as (NG-3896). */
  async getRoleEligibility(
    slug: string,
    roleId: string,
    config?: ApiRequestConfig,
  ): Promise<TRoleEligibility> {
    return this.get(`/api/workspaces/${slug}/custom-roles/${roleId}/eligibility/`, config);
  }

  /** Live, not drafted: saves at once and needs no Publish (NG-3896). */
  async updateRoleEligibility(
    slug: string,
    roleId: string,
    eligibility: TRoleEligibilityAnswers,
  ): Promise<TRoleEligibility> {
    return this.patch(`/api/workspaces/${slug}/custom-roles/${roleId}/eligibility/`, {
      eligibility,
    });
  }

  /** Moves only the Default badge; never publishes the role's draft (NG-1902). */
  async setDefaultRole(slug: string, roleId: string): Promise<ICustomRole> {
    return this.post(`/api/workspaces/${slug}/custom-roles/${roleId}/set-default/`, {});
  }

  async deleteRole(slug: string, roleId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/custom-roles/${roleId}/`);
  }
}
